import '@servicetitan/anvil2/assets/css-utils/utils.css';

import { provide, Provider, useDependencies } from '@servicetitan/react-ioc';
import { observer } from 'mobx-react';
import { useEffect, useRef, useState } from 'react';
import {
  AssistantMessage,
  ChatWindow,
  Content,
  Footer,
  Header,
  MarkdownMessage,
  SuggestionList,
  Thinking,
  UserMessage,
} from '@servicetitan/anvil2-ext-atlas';

import {
  ManagerAssistChatStore,
  USER_ID_ChatStore,
} from './stores/managerAssistChat.store';
import { Stack } from '@servicetitan/design-system';
import {
  AXIOS_TOKEN_InteractionApi,
  BASE_URL_TOKEN_InteractionApi,
  CreatedByType,
  InteractionApi,
  Message,
  USER_ID_InteractionApi,
} from './api/generated/aisInteraction.api';
import { AxiosStore } from './stores/axios.store';
import environment from '@lib/environment';
import useAuth from '@modules/AuthModule/hooks/useAuth';
import { WelcomeScreen } from './welcome/welcome-screen';
import { managerAssistChatLocators } from '@locators/manager-assist-chat';

import styles from './ManagerAssistChat.module.css';

interface ChatProps {
  onClose?: () => void;
}

const Chat = observer(({ onClose }: ChatProps) => {
  const [store] = useDependencies(ManagerAssistChatStore);
  const [inputValue, setInputValue] = useState('');
  // ChatComposerRich (anvil2-ext-atlas) calls onMessageChange and onSubmit
  // back-to-back in the same synchronous handler, before React commits the
  // setInputValue update - reading inputValue in handleSubmitMessage would
  // see the pre-submit (stale) value instead of what was just typed. A ref
  // updates immediately, so it's always current when onSubmit fires.
  const inputValueRef = useRef('');

  // The cleanup function to dispose the store when the component unmounts
  useEffect(() => {
    return () => {
      store.dispose();
    };
  }, [store]);

  const handleSubmitSuggestion = async (suggestion: string) => {
    await store.handleSubmitMessage(suggestion);
  };

  const handleMessageChange = (value: string) => {
    inputValueRef.current = value;
    setInputValue(value);
  };

  const handleSubmitMessage = async () => {
    const text = inputValueRef.current;
    inputValueRef.current = '';
    setInputValue('');
    await store.handleSubmitMessage(text);
  };

  const handleRetry = async () => {
    await store.retry();
  };

  const MessageList = () => (
    <>
      {store.messages.map((message: Message) => {
        if (
          message.createdBy.type === CreatedByType.User &&
          message.details?.text
        ) {
          return (
            <UserMessage key={message.id} message={message.details.text} />
          );
        }
        if (
          message.createdBy.type !== CreatedByType.User &&
          message.details?.text
        ) {
          return (
            <MarkdownMessage
              key={message.id}
              message={message.details?.text}
              className={styles.messageDetails}
            />
          );
        }
        return null; // Explicit handling of edge cases
      })}
    </>
  );

  return (
    <Stack
      id='ManagerAssistChat-Container'
      data-cy='managerAssistChat'
      style={{
        position: 'absolute',
        right: 0,
        top: 10,
      }}
    >
      <ChatWindow open isDragging={false}>
        <Header title='Atlas' onClose={onClose} />
        {!store.bannerAccepted && (
          <WelcomeScreen
            onConfirmHeadsUpBanner={store.handleConfirmHeadsUpBanner}
          />
        )}
        {store.bannerAccepted && (
          <>
            <Content itemsLength={store.messages.length}>
              <MessageList />
              {store.isLoading && <Thinking />}
              {store.errorMessage && (
                <AssistantMessage
                  error={store.errorMessage ?? undefined}
                  message={store.errorMessage ?? undefined}
                  onRetry={store.enableRetry ? handleRetry : undefined}
                />
              )}
            </Content>
            <SuggestionList
              suggestions={store.suggestions}
              onSelect={handleSubmitSuggestion}
            />
            <Footer
              isLoading={store.isLoading}
              message={inputValue}
              onMessageChange={handleMessageChange}
              onSubmit={handleSubmitMessage}
              messageInputId={
                managerAssistChatLocators.managerAssistChat.chat.messageInput
              }
              sendIconId={
                managerAssistChatLocators.managerAssistChat.chat.sendButton
              }
            />
          </>
        )}
      </ChatWindow>
    </Stack>
  );
});

export interface ManagerAssistChatProps {
  isActive: boolean;
  onClose?: () => void;
}

export const ManagerAssistChat = provide({
  singletons: [AxiosStore],
  instances: [ManagerAssistChatStore],
})(
  observer(({ onClose, isActive }: ManagerAssistChatProps) => {
    const [{ axiosInstance }] = useDependencies(AxiosStore);
    const { user } = useAuth();
    const userId = user?.id ?? '0';

    return (
      isActive && (
        <Provider
          singletons={[
            InteractionApi,
            {
              provide: BASE_URL_TOKEN_InteractionApi,
              useValue: environment.AIS_API_URL,
            },
            { provide: AXIOS_TOKEN_InteractionApi, useValue: axiosInstance },
            { provide: USER_ID_InteractionApi, useValue: userId },
            { provide: USER_ID_ChatStore, useValue: userId },
          ]}
        >
          <Chat onClose={onClose} />
        </Provider>
      )
    );
  }),
);
